import { Link } from "react-router-dom";
import { useTranslation } from "../contexts/TranslationContext.jsx";
import {
  Monitor,
  Mic,
  Eye,
  EyeOff,
  MousePointerClick,
  Brain,
  FileText,
  Layers,
  Camera,
  Users,
  CircleCheck,
  CircleX,
  Circle,
  ChevronRight,
  Shield,
  Zap,
  Code,
  Presentation,
  Headphones,
  Scale,
  GraduationCap,
  MessageSquare,
  BarChart3,
  Globe,
  Accessibility,
  User,
  ArrowUp,
} from "lucide-react";

/* ──────────────────────── Section Label (yellow dot + uppercase) ──────────────────────── */
function SectionLabel({ text }) {
  return (
    <div className="flex items-center gap-2 mb-4">
      <span className="w-2 h-2 rounded-full bg-brand-500 inline-block" />
      <span className="text-xs tracking-[0.2em] uppercase text-gray-400 font-mono">
        {text}
      </span>
    </div>
  );
}

/* ──────────────────────── Comparison Check / X ──────────────────────── */
function Check() {
  return (
    <div className="flex items-center justify-center">
      <CircleCheck size={20} className="text-brand-500" />
    </div>
  );
}
function Cross() {
  return (
    <div className="flex items-center justify-center">
      <CircleX size={20} className="text-red-500/60" />
    </div>
  );
}
function Neutral() {
  return (
    <div className="flex items-center justify-center">
      <Circle size={20} className="text-gray-600" />
    </div>
  );
}

/* ──────────────────────── Feature Card ──────────────────────── */
function FeatureCard({ icon: Icon, title, description, status, accentColor = "yellow", t }) {
  const colorMap = {
    yellow: "text-brand-500 border-brand-500/20",
    blue: "text-blue-400 border-blue-400/20",
    emerald: "text-emerald-400 border-emerald-400/20",
    purple: "text-purple-400 border-purple-400/20",
  };

  const statusColors = {
    implemented: "bg-green-500/10 text-green-400",
    inProgress: "bg-brand-500/10 text-brand-400",
    pending: "bg-gray-500/10 text-gray-500",
  };

  const statusIcons = {
    implemented: <CircleCheck size={12} className="inline mr-1" />,
    inProgress: <Circle size={12} className="inline mr-1" />,
    pending: <CircleX size={12} className="inline mr-1" />,
  };

  const colors = colorMap[accentColor] || colorMap.yellow;

  return (
    <div className="group p-6 bg-[#111111] border border-white/5 rounded-2xl hover:border-white/10 transition-all duration-300 hover:bg-[#161616]">
      <div className={`mb-4 ${colors.split(" ")[0]}`}>
        <Icon size={24} />
      </div>
      <h3 className="text-lg font-semibold text-white mb-2">{title}</h3>
      <p className="text-sm text-gray-400 leading-relaxed mb-3">{description}</p>
      {status && (
        <span className={`text-xs px-2 py-1 rounded-full inline-flex items-center ${statusColors[status]}`}>
          {statusIcons[status]}
          {t ? (status === "implemented" ? t("features.status.implemented") : status === "inProgress" ? t("features.status.onDevelopment") : t("features.status.pending")) : status}
        </span>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════ */
/*                           HOME PAGE                                  */
/* ══════════════════════════════════════════════════════════════════════ */

export default function Home() {
  const { t } = useTranslation();

  const comparisonFeatures = [
    { key: "supportsAudio", icon: Mic, wisper: true, comp1: false, comp2: false, comp3: false },
    { key: "invisibleScreenShare", icon: Monitor, wisper: true, comp1: true, comp2: true, comp3: true },
    { key: "invisibleTaskbar", icon: EyeOff, wisper: true, comp1: false, comp2: false, comp3: false },
    { key: "invisibleTaskManager", icon: Shield, wisper: true, comp1: false, comp2: false, comp3: false },
    { key: "clickThrough", icon: MousePointerClick, wisper: true, comp1: false, comp2: false, comp3: false },
    { key: "aiResponses", icon: Brain, wisper: true, comp1: true, comp2: true, comp3: true },
    { key: "codeReview", icon: Code, wisper: true, comp1: false, comp2: false, comp3: false },
    { key: "aiResume", icon: FileText, wisper: true, comp1: false, comp2: false, comp3: false },
    { key: "resumeContext", icon: FileText, wisper: true, comp1: false, comp2: false, comp3: false },
    { key: "multiContext", icon: Layers, wisper: true, comp1: false, comp2: false, comp3: false },
    { key: "screenshotSolver", icon: Camera, wisper: true, comp1: false, comp2: false, comp3: false },
    { key: "xrayMode", icon: Eye, wisper: true, comp1: false, comp2: false, comp3: false },
    { key: "meetingCompanion", icon: Users, wisper: true, comp1: false, comp2: false, comp3: false },
  ];

  const allFeatures = [
    // Highlight features
    { icon: Shield, titleKey: "stealthMode", accentColor: "yellow", status: "implemented" },
    { icon: Users, titleKey: "meetingCompanion", accentColor: "blue", status: "implemented" },
    { icon: Code, titleKey: "codeReviewer", accentColor: "emerald", status: "implemented" },
    { icon: Camera, titleKey: "screenshotSolver", accentColor: "purple", status: "implemented" },
    // Implemented
    { icon: Presentation, titleKey: "presentationAssistant", status: "implemented" },
    { icon: FileText, titleKey: "curriculoIA", status: "implemented" },
    { icon: Headphones, titleKey: "techSupport", status: "implemented" },
    { icon: Zap, titleKey: "codingAssistant", status: "implemented" },
    { icon: Camera, titleKey: "smartCapture", status: "implemented" },
    { icon: GraduationCap, titleKey: "oralExamHelp", status: "implemented" },
    { icon: Eye, titleKey: "xrayMode", status: "implemented" },
    { icon: MessageSquare, titleKey: "executiveCoach", status: "implemented" },
    { icon: Scale, titleKey: "legalSupport", status: "implemented" },
    { icon: Accessibility, titleKey: "inclusionTool", status: "implemented" },
    // In progress
    { icon: User, titleKey: "speakerCoach", status: "inProgress" },
    { icon: GraduationCap, titleKey: "educationTrainer", status: "inProgress" },
    // Pending
    { icon: Globe, titleKey: "translator", status: "pending" },
    { icon: User, titleKey: "avatarAssistant", status: "pending" },
    { icon: BarChart3, titleKey: "insightsDashboard", status: "pending" },
    { icon: MessageSquare, titleKey: "careerCoach", status: "pending" },
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white overflow-hidden">
      {/* ━━━━━━━━━━━━━━━ HERO ━━━━━━━━━━━━━━━ */}
      <section className="relative flex flex-col items-center justify-center text-center px-4 pt-20 pb-24 md:pt-32 md:pb-36">
        {/* Glow background effect */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-brand-500/5 rounded-full blur-[120px] pointer-events-none" />

        {/* Badge */}
        <div className="relative flex items-center gap-2 mb-8 px-4 py-2 rounded-full border border-white/10 bg-white/5 backdrop-blur-sm">
          <span className="w-2 h-2 rounded-full bg-brand-500 animate-pulse" />
          <span className="text-xs tracking-[0.15em] uppercase text-gray-300 font-mono">
            {t("home.badge")}
          </span>
          <ChevronRight size={14} className="text-gray-500" />
        </div>

        {/* Headline */}
        <h1 className="relative text-4xl sm:text-5xl md:text-7xl font-bold leading-tight max-w-4xl">
          {t("home.heroLine1")}{" "}
          <span className="text-brand-500">{t("home.heroLine2")}</span>{" "}
          {t("home.heroLine3")}
          <br className="hidden sm:block" />{" "}
          {t("home.heroLine4")}
        </h1>

        {/* Subtitle */}
        <p className="mt-6 text-base sm:text-lg text-gray-400 max-w-2xl leading-relaxed">
          {t("home.subtitle")}
        </p>

        {/* CTA Button - público */}
        <div className="mt-8 flex justify-center w-full max-w-sm sm:max-w-none sm:w-auto">
          <Link to="/register" className="w-full sm:w-auto">
            <button className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-brand-500 hover:bg-brand-400 text-black font-semibold transition-all duration-200 hover:scale-105">
              <ArrowUp size={18} className="rotate-45" />
              {t("nav.createAccount")}
            </button>
          </Link>
        </div>

      </section>

      {/* ━━━━━━━━━━━━━━━ APP DEMO IMAGES ━━━━━━━━━━━━━━━ */}
      <section className="relative max-w-5xl mx-auto px-4 pb-20">
        <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl shadow-brand-500/5">
          <img
            src="/images/1.png"
            alt="SecretDev App Demo"
            className="w-full object-cover"
          />
        </div>
      </section>

      {/* ━━━━━━━━━━━━━━━ DIVIDER ━━━━━━━━━━━━━━━ */}
      <div className="w-full border-t border-white/5" />

      {/* ━━━━━━━━━━━━━━━ HOW IT WORKS ━━━━━━━━━━━━━━━ */}
      <section className="max-w-6xl mx-auto px-4 py-20 md:py-32">
        <SectionLabel text={t("home.howItWorksLabel")} />
        <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-16 max-w-2xl">
          {t("home.howItWorksTitle")}
        </h2>

        <div className="grid md:grid-cols-3 gap-6">
          {/* Card 1 - Stealth */}
          <div className="p-6 rounded-2xl bg-gradient-to-br from-brand-500/10 to-transparent border border-brand-500/20 hover:border-brand-500/40 transition-all duration-300">
            <div className="w-12 h-12 rounded-xl bg-brand-500/10 flex items-center justify-center mb-4">
              <Shield size={24} className="text-brand-500" />
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">
              {t("features.stealthMode.title")}
            </h3>
            <p className="text-sm text-gray-400 leading-relaxed">
              {t("features.stealthMode.description")}
            </p>
          </div>

          {/* Card 2 - Audio Capture */}
          <div className="p-6 rounded-2xl bg-gradient-to-br from-blue-500/10 to-transparent border border-blue-500/20 hover:border-blue-500/40 transition-all duration-300">
            <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center mb-4">
              <Mic size={24} className="text-blue-400" />
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">
              {t("features.meetingCompanion.title")}
            </h3>
            <p className="text-sm text-gray-400 leading-relaxed">
              {t("features.meetingCompanion.description")}
            </p>
          </div>

          {/* Card 3 - Screenshot */}
          <div className="p-6 rounded-2xl bg-gradient-to-br from-emerald-500/10 to-transparent border border-emerald-500/20 hover:border-emerald-500/40 transition-all duration-300">
            <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center mb-4">
              <Camera size={24} className="text-emerald-400" />
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">
              {t("features.screenshotSolver.title")}
            </h3>
            <p className="text-sm text-gray-400 leading-relaxed">
              {t("features.screenshotSolver.description")}
            </p>
          </div>
        </div>
      </section>

      {/* ━━━━━━━━━━━━━━━ DIVIDER ━━━━━━━━━━━━━━━ */}
      <div className="w-full border-t border-white/5" />

      {/* ━━━━━━━━━━━━━━━ COMPARISON TABLE ━━━━━━━━━━━━━━━ */}
      <section className="max-w-6xl mx-auto px-4 py-20 md:py-32">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between mb-12">
          <div>
            <SectionLabel text={t("home.proofLabel")} />
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white max-w-lg">
              {t("home.proofTitle")}
            </h2>
          </div>
          <p className="text-gray-400 max-w-sm mt-4 lg:mt-8 leading-relaxed">
            {t("home.proofSubtitle")}
          </p>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left py-4 px-4 text-sm text-gray-400 font-normal">
                  {t("home.comparisonFeatures")}
                </th>
                <th className="py-4 px-4 text-center">
                  <div className="flex flex-col items-center gap-1">
                    <span className="text-brand-500 font-bold text-lg">SecretDev</span>
                  </div>
                </th>
                <th className="py-4 px-4 text-center text-gray-500 text-sm">Tool A</th>
                <th className="py-4 px-4 text-center text-gray-500 text-sm">Tool B</th>
                <th className="py-4 px-4 text-center text-gray-500 text-sm">Tool C</th>
              </tr>
            </thead>
            <tbody>
              {comparisonFeatures.map((feat, i) => (
                <tr key={i} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                  <td className="py-4 px-4 flex items-center gap-3 text-sm text-gray-300">
                    <feat.icon size={16} className="text-gray-500" />
                    {t(`home.comparison.${feat.key}`)}
                  </td>
                  <td className="py-4 px-4 bg-brand-500/[0.03]">
                    <Check />
                  </td>
                  <td className="py-4 px-4">
                    {feat.comp1 ? <Check /> : <Cross />}
                  </td>
                  <td className="py-4 px-4">
                    {feat.comp2 ? <Check /> : <Cross />}
                  </td>
                  <td className="py-4 px-4">
                    {feat.comp3 ? <Check /> : <Cross />}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* ━━━━━━━━━━━━━━━ DIVIDER ━━━━━━━━━━━━━━━ */}
      <div className="w-full border-t border-white/5" />

      {/* ━━━━━━━━━━━━━━━ ALL FEATURES ━━━━━━━━━━━━━━━ */}
      <section className="max-w-6xl mx-auto px-4 py-20 md:py-32">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between mb-12">
          <div>
            <SectionLabel text={t("home.spotlightLabel")} />
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white max-w-lg">
              {t("home.spotlightTitle")}
            </h2>
          </div>
          <p className="text-gray-400 max-w-sm mt-4 lg:mt-8 leading-relaxed">
            {t("home.spotlightSubtitle")}
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {allFeatures.map((feat, i) => (
            <FeatureCard
              key={i}
              icon={feat.icon}
              title={t(`features.${feat.titleKey}.title`)}
              description={t(`features.${feat.titleKey}.description`)}
              status={feat.status}
              accentColor={feat.accentColor}
              t={t}
            />
          ))}
        </div>
      </section>

      {/* ━━━━━━━━━━━━━━━ DIVIDER ━━━━━━━━━━━━━━━ */}
      <div className="w-full border-t border-white/5" />

      {/* ━━━━━━━━━━━━━━━ DYNAMIC CONTEXTS ━━━━━━━━━━━━━━━ */}
      <section className="max-w-6xl mx-auto px-4 py-20 md:py-32">
        <SectionLabel text={t("home.contextsLabel")} />
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between mb-12">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white max-w-lg">
            {t("home.contextsTitle")}
          </h2>
          <p className="text-gray-400 max-w-sm mt-4 lg:mt-8 leading-relaxed">
            {t("home.contextsSubtitle")}
          </p>
        </div>

        <div className="flex flex-col gap-6">
          {["/images/2.png", "/images/3.png"].map((src, i) => (
            <div key={i} className="rounded-2xl overflow-hidden border border-white/10 shadow-2xl shadow-black/50">
              <img
                src={src}
                alt={`SecretDev Context ${i + 1}`}
                className="w-full object-cover"
              />
            </div>
          ))}
        </div>
      </section>

      {/* ━━━━━━━━━━━━━━━ DIVIDER ━━━━━━━━━━━━━━━ */}
      <div className="w-full border-t border-white/5" />

      {/* ━━━━━━━━━━━━━━━ CTA FINAL ━━━━━━━━━━━━━━━ */}
      <section className="relative max-w-4xl mx-auto px-4 py-20 md:py-32 text-center">
        {/* Glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-brand-500/5 rounded-full blur-[100px] pointer-events-none" />

        <h2 className="relative text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
          {t("home.ctaTitle")}
        </h2>
        <p className="relative text-gray-400 text-lg mb-8 max-w-xl mx-auto">
          {t("home.ctaSubtitle")}
        </p>

        <div className="relative flex flex-col sm:flex-row gap-4 justify-center w-full max-w-sm sm:max-w-none mx-auto">
          <Link to="/register" className="w-full sm:w-auto">
            <button className="w-full sm:w-auto flex items-center justify-center gap-2 px-8 py-4 rounded-lg bg-brand-500 hover:bg-brand-400 text-black font-bold text-lg transition-all duration-200 hover:scale-105">
              <ArrowUp size={20} className="rotate-45" />
              {t("nav.createAccount")}
            </button>
          </Link>
        </div>

        {/* Manifesto */}
        <p className="relative mt-16 text-sm text-gray-500 leading-relaxed italic max-w-2xl mx-auto">
          "{t("home.manifesto")}"
        </p>
      </section>

      {/* ━━━━━━━━━━━━━━━ FOOTER ━━━━━━━━━━━━━━━ */}
      <div className="border-t border-white/5 py-8 text-center text-gray-600 text-xs tracking-wider">
        {t("home.footer")}
      </div>
    </div>
  );
}
